import { useCallback, useEffect, useState } from "react";
import { Check, Edit3, Save, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function LimitInput({ label, value, onChange, testId }) {
  const isUnlim = value === null || value === undefined;
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</label>
      <div className="mt-1 flex gap-2">
        <input
          type="number"
          min="0"
          disabled={isUnlim}
          value={isUnlim ? "" : value}
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
          data-testid={testId}
          className="w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100 disabled:opacity-50 focus:border-amber-500 focus:outline-none"
        />
        <label className="inline-flex items-center gap-1 text-[11px] text-slate-400">
          <input type="checkbox" checked={isUnlim} onChange={(e) => onChange(e.target.checked ? null : 0)} className="accent-amber-500" />
          Unlim
        </label>
      </div>
    </div>
  );
}

function PlanEditor({ plan, onClose, onSaved }) {
  const [form, setForm] = useState(plan);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      const { data } = await api.patch(`/platform/subscription-plans/${plan.id}`, {
        name_en: form.name_en,
        name_sv: form.name_sv,
        name_ar: form.name_ar,
        description_en: form.description_en,
        description_sv: form.description_sv,
        description_ar: form.description_ar,
        price_monthly: Number(form.price_monthly),
        price_yearly: Number(form.price_yearly),
        currency: form.currency,
        limits: form.limits,
        features: form.features,
        is_active: form.is_active,
      });
      toast.success("Plan saved");
      onSaved(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="plan-editor-modal">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-slate-700 bg-slate-950 p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Edit plan · <span className="text-amber-400">{form.code}</span></h3>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-800 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {["en", "sv", "ar"].map((lang) => (
              <div key={lang}>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Name ({lang.toUpperCase()})</label>
                <input
                  value={form[`name_${lang}`] || ""}
                  onChange={(e) => setForm({ ...form, [`name_${lang}`]: e.target.value })}
                  data-testid={`plan-editor-name-${lang}`}
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100 focus:border-amber-500 focus:outline-none"
                />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {["en", "sv", "ar"].map((lang) => (
              <div key={lang}>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Description ({lang.toUpperCase()})</label>
                <textarea
                  rows={2}
                  value={form[`description_${lang}`] || ""}
                  onChange={(e) => setForm({ ...form, [`description_${lang}`]: e.target.value })}
                  data-testid={`plan-editor-desc-${lang}`}
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100 focus:border-amber-500 focus:outline-none"
                />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Monthly ({form.currency || "SEK"})</label>
              <input type="number" min="0" step="0.01" value={form.price_monthly} onChange={(e) => setForm({ ...form, price_monthly: e.target.value })} data-testid="plan-editor-price-monthly" className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100 focus:border-amber-500 focus:outline-none" />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Yearly ({form.currency || "SEK"})</label>
              <input type="number" min="0" step="0.01" value={form.price_yearly} onChange={(e) => setForm({ ...form, price_yearly: e.target.value })} data-testid="plan-editor-price-yearly" className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100 focus:border-amber-500 focus:outline-none" />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Currency</label>
              <input value={form.currency || "SEK"} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100 focus:border-amber-500 focus:outline-none" />
            </div>
          </div>

          <div>
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Limits</div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <LimitInput label="Invoices/week" value={form.limits?.invoices_per_week} onChange={(v) => setForm({ ...form, limits: { ...form.limits, invoices_per_week: v } })} testId="plan-editor-limit-invoices" />
              <LimitInput label="Quotes/week" value={form.limits?.quotes_per_week} onChange={(v) => setForm({ ...form, limits: { ...form.limits, quotes_per_week: v } })} testId="plan-editor-limit-quotes" />
              <LimitInput label="Users max" value={form.limits?.users_max} onChange={(v) => setForm({ ...form, limits: { ...form.limits, users_max: v } })} testId="plan-editor-limit-users" />
              <LimitInput label="Customers max" value={form.limits?.customers_max} onChange={(v) => setForm({ ...form, limits: { ...form.limits, customers_max: v } })} testId="plan-editor-limit-customers" />
            </div>
          </div>

          <div>
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Features (toggle inclusion)</div>
            <div className="space-y-1.5">
              {(form.features || []).map((f, idx) => (
                <label key={f.key} className="flex items-center gap-3 rounded-md border border-slate-800 bg-slate-900/50 px-3 py-2 text-sm">
                  <input type="checkbox" checked={!!f.included} onChange={(e) => {
                    const next = [...form.features];
                    next[idx] = { ...next[idx], included: e.target.checked };
                    setForm({ ...form, features: next });
                  }} className="accent-amber-500" data-testid={`plan-editor-feature-${f.key}`} />
                  <span className="text-slate-300">{f.label_en}</span>
                  <code className="ms-auto text-[10px] text-slate-500">{f.key}</code>
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-3">
            <button onClick={onClose} className="rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800">Cancel</button>
            <button onClick={save} disabled={busy} data-testid="plan-editor-save" className="inline-flex items-center gap-1 rounded-md bg-amber-500 px-3 py-1.5 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60">
              <Save className="h-3.5 w-3.5" /> Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminSubscriptions() {
  const [plans, setPlans] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, m] = await Promise.all([
        api.get("/platform/subscription-plans"),
        api.get("/platform/metrics"),
      ]);
      setPlans(p.data.items);
      setCounts(m.data.tenants_by_plan || m.data.plans || {});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="admin-subscriptions-page">
      <header className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500/15 text-amber-300 ring-1 ring-inset ring-amber-500/30">
          <Sparkles className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">Subscription plans</h1>
          <p className="mt-1 text-sm text-slate-400">Live editable plans — prices, limits, features per language.</p>
        </div>
      </header>

      <div className="mobile-cards overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/70">
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-4 py-3 text-start">Plan</th>
              <th className="px-4 py-3 text-end">Monthly</th>
              <th className="px-4 py-3 text-end">Yearly</th>
              <th className="px-4 py-3 text-start">Invoices/wk</th>
              <th className="px-4 py-3 text-start">Quotes/wk</th>
              <th className="px-4 py-3 text-start">Users</th>
              <th className="px-4 py-3 text-end">Tenants</th>
              <th className="px-4 py-3 text-end">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="admin-subs-table">
            {loading && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>}
            {!loading && plans.map((p) => (
              <tr key={p.id} className="border-t border-slate-800" data-testid={`plan-row-${p.code}`}>
                <td className="px-4 py-3" data-label="Plan">
                  <div className="font-semibold capitalize text-white">{p.name_en}</div>
                  <div className="text-xs text-slate-500 font-mono">{p.code}</div>
                </td>
                <td className="px-4 py-3 text-end tabular-nums" data-label="Monthly">{p.price_monthly} {p.currency}</td>
                <td className="px-4 py-3 text-end tabular-nums" data-label="Yearly">{p.price_yearly} {p.currency}</td>
                <td className="px-4 py-3" data-label="Invoices/wk">{p.limits?.invoices_per_week ?? "∞"}</td>
                <td className="px-4 py-3" data-label="Quotes/wk">{p.limits?.quotes_per_week ?? "∞"}</td>
                <td className="px-4 py-3" data-label="Users">{p.limits?.users_max ?? "∞"}</td>
                <td className="px-4 py-3 text-end tabular-nums font-semibold" data-label="Tenants">{counts[p.code] || 0}</td>
                <td className="px-4 py-3 text-end" data-label="Actions">
                  <button onClick={() => setEditing(p)} data-testid={`plan-edit-${p.code}`} className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-xs text-amber-300 hover:bg-amber-500/20">
                    <Edit3 className="h-3 w-3" /> Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <PlanEditor plan={editing} onClose={() => setEditing(null)} onSaved={(updated) => {
          setPlans((prev) => prev.map((p) => p.id === updated.id ? updated : p));
          setEditing(null);
        }} />
      )}
    </div>
  );
}
